import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, CalendarClock, Check, Loader2, Save, Send, Tag, ImageIcon, X } from "lucide-react";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import { ModulePanel } from "@/components/admin/AdminPrimitives";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { MultiSelectDropdown } from "@/components/admin/MultiSelectDropdown";
import { useI18n } from "@/lib/i18n";
import { useCreateArticle, useUpdateArticle } from "@/hooks/useArticles";
import { useCategories } from "@/hooks/useCategories";
import { useEtiquettes } from "@/hooks/useEtiquettes";
import { useRubriques } from "@/hooks/useRubriques";
import { useTypesArticle } from "@/hooks/useTypesArticle";
import { usePays } from "@/hooks/usePays";
import { useUploadImage } from "@/hooks/useFileUpload";
import { ArticleDto } from "@/types/article.types";
import {
  SIGNATURE_OPTIONS,
  langToApi,
  generateSlug,
} from "@/lib/enums";
import { useBloquerPartenaire } from "@/pages/admin/RequireAdmin";

function ArticleNouveau() {
  useBloquerPartenaire();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const createArticle = useCreateArticle();
  const updateArticle = useUpdateArticle();
  const uploadImage = useUploadImage();

  const { data: categories } = useCategories();
  const { data: etiquettes } = useEtiquettes();
  const { data: rubriques } = useRubriques();
  const { data: typesArticle } = useTypesArticle();
  const { data: pays } = usePays();
  const categoryList = Array.isArray(categories) ? categories : [];
  const etiquetteList = Array.isArray(etiquettes) ? etiquettes : [];
  const rubriqueList = Array.isArray(rubriques) ? rubriques : [];
  const typeArticleList = Array.isArray(typesArticle) ? typesArticle : [];
  const paysList = Array.isArray(pays) ? pays : [];

  const [rubriqueId, setRubriqueId] = useState("");
  const [selectedPays, setSelectedPays] = useState<number[]>([]);
  const [subventionne, setSubventionne] = useState(false);
  const [assisteParIa, setAssisteParIa] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [type, setType] = useState("ARTICLE");
  const [signature, setSignature] = useState("REDACTION");
  const [selectedEtiquettes, setSelectedEtiquettes] = useState<number[]>([]);
  const [commentairesAutorises, setCommentairesAutorises] = useState(true);

  const [titreFr, setTitreFr] = useState("");
  const [sousTitreFr, setSousTitreFr] = useState("");
  const [corpsFr, setCorpsFr] = useState("");

  const [programmerPour, setProgrammerPour] = useState("");
  const estProgramme = !!programmerPour && new Date(programmerPour) > new Date();
  const nowLocal = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };
  const [dateCreation, setDateCreation] = useState(nowLocal());
  const [dateModification, setDateModification] = useState(nowLocal());
  const [imageCouverture, setImageCouverture] = useState("");
  const [metaTitre, setMetaTitre] = useState("");
  const [metaDescription, setMetaDescription] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [coverImageError, setCoverImageError] = useState(false);

  // Enregistrement automatique : dès que rubrique + titre sont renseignés, un
  // brouillon est silencieusement créé en base après 3s d'inactivité, puis mis
  // à jour à chaque changement — ainsi l'article existe réellement même si on
  // quitte la page sans cliquer sur "Publier" / "Enregistrer en brouillon".
  const [createdId, setCreatedIdState] = useState<number | null>(null);
  // Miroir synchrone de createdId : à l'intérieur d'une closure déjà lancée
  // (handleSave après avoir attendu autoSaveInFlight), l'état React capturé
  // reste celui du moment de l'appel — la ref, elle, est à jour immédiatement.
  const createdIdRef = useRef<number | null>(null);
  const setCreatedId = (id: number | null) => {
    createdIdRef.current = id;
    setCreatedIdState(id);
  };
  const [lastAutoSaveAt, setLastAutoSaveAt] = useState<Date | null>(null);
  const skipNextAutoSave = useRef(true);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoSaveEnCours = useRef(false);
  // Requête d'auto-save actuellement en vol (si aucune : null) — permet à
  // handleSave d'attendre sa fin plutôt que de partir en parallèle avec le
  // même slug, ce qui causait la double publication/création.
  const autoSaveInFlight = useRef<Promise<void> | null>(null);

  const rubriqueSelectionnee = rubriqueList.find((r: any) => r.id === Number(rubriqueId));
  const slugApercu = titreFr.trim()
    ? generateSlug(titreFr, rubriqueSelectionnee?.slug || "")
    : "";

  // L'auto-save exige un titre + une rubrique (le slug ne peut pas être généré
  // sans ça). Tant que ce n'est pas le cas, on invite explicitement l'utilisateur
  // à les renseigner plutôt que de laisser l'enregistrement automatique échouer
  // silencieusement.
  const aCommenceLeRemplissage =
    !!rubriqueId || selectedPays.length > 0 || selectedCategories.length > 0 || selectedEtiquettes.length > 0 ||
    !!sousTitreFr.trim() || !!corpsFr.trim();
  const champsManquantsAutoSave: string[] = [];
  if (!titreFr.trim()) champsManquantsAutoSave.push("un titre");
  if (!rubriqueId) champsManquantsAutoSave.push("une rubrique");
  const autoSaveIncomplete = createdId == null && aCommenceLeRemplissage && champsManquantsAutoSave.length > 0;

  const construirePayloadBase = () => ({
    slug: slugApercu,
    rubriqueId: Number(rubriqueId),
    paysIds: selectedPays,
    subventionne,
    assisteParIa,
    categorieIds: selectedCategories,
    commentairesAutorises,
    imageCouverture: imageCouverture || undefined,
    metaTitre: metaTitre.trim() || undefined,
    metaDescription: metaDescription.trim() || undefined,
    // Un article programmé n'a pas de date de création/modification "réelle" tant
    // qu'il n'est pas effectivement publié — le backend les fixe lui-même à ce moment-là.
    creeLe: !estProgramme && dateCreation ? new Date(dateCreation).toISOString() : undefined,
    modifieLe: !estProgramme && dateModification ? new Date(dateModification).toISOString() : undefined,
    type,
    signature,
    etiquetteIds: selectedEtiquettes,
    traductions: [
      {
        langue: langToApi("fr"),
        titre: titreFr.trim(),
        sousTitre: sousTitreFr.trim() || undefined,
        corps: corpsFr.trim() || undefined,
      },
    ],
  });

  useEffect(() => {
    if (skipNextAutoSave.current) {
      skipNextAutoSave.current = false;
      return;
    }
    if (!rubriqueId || !titreFr.trim()) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      if (autoSaveEnCours.current) return;
      autoSaveEnCours.current = true;
      autoSaveInFlight.current = (async () => {
        try {
          if (createdIdRef.current == null) {
            const created: ArticleDto = await createArticle.mutateAsync({
              ...construirePayloadBase(),
              statut: "BROUILLON",
            });
            setCreatedId(created.id);
          } else {
            await updateArticle.mutateAsync({ id: createdIdRef.current, payload: construirePayloadBase() });
          }
          setLastAutoSaveAt(new Date());
        } catch {
          // Échec silencieux : l'auto-save réessaiera à la prochaine modification.
        } finally {
          autoSaveEnCours.current = false;
          autoSaveInFlight.current = null;
        }
      })();
    }, 3000);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rubriqueId, selectedPays, subventionne, assisteParIa, selectedCategories, selectedEtiquettes,
    type, signature, commentairesAutorises, imageCouverture, metaTitre,
    metaDescription, titreFr, sousTitreFr, corpsFr, dateCreation, dateModification,
  ]);

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const result = await uploadImage.mutateAsync(file);
      setCoverImageError(false);
      setImageCouverture(result.url);
    } catch {
      setError(t("admin.common.uploadError"));
    }
  };

  const handleSave = async (statutCible: "BROUILLON" | "PUBLIE") => {
    if (!rubriqueId) {
      setError(t("admin.article.selectRubrique"));
      return;
    }
    if (!titreFr.trim()) {
      setError(t("admin.article.titleRequired"));
      return;
    }

    // Empêche l'auto-save de tourner en même temps et de créer un doublon :
    // annule le minuteur en attente, ET attend une requête déjà en vol (le
    // minuteur a pu se déclencher juste avant le clic) pour que createdIdRef
    // soit à jour avant de décider création vs mise à jour.
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    if (autoSaveInFlight.current) await autoSaveInFlight.current;

    setSaving(true);
    setError("");
    try {
      const publieLe = programmerPour ? new Date(programmerPour).toISOString() : undefined;
      // Une publication programmée dans le futur reste "PROGRAMME" jusqu'à la date choisie.
      const statutEffectif =
        statutCible === "PUBLIE" && publieLe && new Date(publieLe) > new Date() ? "PROGRAMME" : statutCible;
      const payload = { ...construirePayloadBase(), publieLe, statut: statutEffectif };

      const resultId = createdIdRef.current != null
        ? (await updateArticle.mutateAsync({ id: createdIdRef.current, payload })).id
        : (await createArticle.mutateAsync(payload as any)).id;

      toast.success(
        statutEffectif === "PROGRAMME"
          ? "Article programmé"
          : statutCible === "PUBLIE"
          ? "Article publié"
          : "Brouillon enregistré"
      );
      navigate(`/redaction/articles/${resultId}`);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || t("admin.common.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AdminTopbar title={t("admin.article.new.title")} subtitle={t("admin.article.new.subtitle")} />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {error && (
          <div className="flex items-center gap-2 rounded-md bg-alert/10 border border-alert/30 px-4 py-2 text-xs text-alert font-medium">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            {error}
          </div>
        )}

        {autoSaveIncomplete && (
          <div className="flex items-center gap-2 rounded-md bg-verified/10 border border-verified/30 px-4 py-2 text-xs text-verified font-medium">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            Ajoutez {champsManquantsAutoSave.join(" et ")} pour que cet article soit automatiquement enregistré en brouillon.
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-6">
            <ModulePanel title={t("admin.article.new.content")}>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground">
                    {t("admin.article.titre")} <span className="text-alert">*</span>
                  </label>
                  <input
                    value={titreFr}
                    onChange={(e) => setTitreFr(e.target.value)}
                    placeholder="Titre de l'article en français"
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground">{t("admin.article.sousTitre")}</label>
                  <input
                    value={sousTitreFr}
                    onChange={(e) => setSousTitreFr(e.target.value)}
                    placeholder="Chapô — résumé en une ou deux phrases…"
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground">{t("admin.article.corps")}</label>
                  <RichTextEditor value={corpsFr} onChange={setCorpsFr} placeholder="Corps de l'article…" autoFocusStart />
                </div>
              </div>
            </ModulePanel>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <ModulePanel title={t("admin.article.new.publication")}>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("admin.article.schedule")}
                  </label>
                  <input
                    type="datetime-local"
                    value={programmerPour}
                    onChange={(e) => setProgrammerPour(e.target.value)}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                  />
                </div>
                {estProgramme ? (
                  <p className="rounded-md bg-surface px-3 py-2 text-[11px] text-muted-foreground">
                    Les dates de création et de modification seront fixées automatiquement à la publication réelle, le {new Date(programmerPour).toLocaleString("fr-FR")}.
                  </p>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {t("admin.article.dateCreation")}
                      </label>
                      <input
                        type="datetime-local"
                        value={dateCreation}
                        onChange={(e) => setDateCreation(e.target.value)}
                        className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {t("admin.article.dateModification")}
                      </label>
                      <input
                        type="datetime-local"
                        value={dateModification}
                        onChange={(e) => setDateModification(e.target.value)}
                        className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                      />
                    </div>
                  </div>
                )}
                <button
                  onClick={() => handleSave("PUBLIE")}
                  disabled={saving}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-verified px-4 py-2.5 text-sm font-semibold text-verified-foreground hover:opacity-90 disabled:opacity-60 transition-opacity"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : estProgramme ? (
                    <CalendarClock className="h-4 w-4" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {estProgramme ? t("admin.article.schedulePublish") : t("admin.article.publish")}
                </button>
                <button
                  onClick={() => handleSave("BROUILLON")}
                  disabled={saving}
                  className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-surface disabled:opacity-60 transition-colors"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {t("admin.article.saveDraft")}
                </button>
                {lastAutoSaveAt && (
                  <p className="flex items-center justify-center gap-1.5 text-[11px] text-verified">
                    <Check className="h-3 w-3" /> Enregistré automatiquement à {lastAutoSaveAt.toLocaleTimeString()}
                  </p>
                )}
              </div>
            </ModulePanel>

            <ModulePanel title={t("admin.article.new.meta")}>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("admin.article.rubrique")} <span className="text-alert">*</span>
                  </label>
                  <select
                    value={rubriqueId}
                    onChange={(e) => setRubriqueId(e.target.value)}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                  >
                    <option value="">{t("admin.article.selectRubrique")}</option>
                    {rubriqueList.map((r: any) => (
                      <option key={r.id} value={r.id}>{locale === "fr" ? r.nom : (r.nomEn || r.nom)}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Pays
                  </label>
                  <MultiSelectDropdown
                    options={paysList.map((p: any) => ({
                      value: p.id,
                      label: locale === "fr" ? p.nom : (p.nomEn || p.nom),
                    }))}
                    selected={selectedPays}
                    onChange={setSelectedPays}
                    placeholder={t("admin.article.select")}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("admin.article.categories")}
                  </label>
                  <MultiSelectDropdown
                    options={categoryList.map((c: any) => ({
                      value: c.id,
                      label: locale === "fr" ? c.libelleFr : c.libelleEn,
                    }))}
                    selected={selectedCategories}
                    onChange={setSelectedCategories}
                    placeholder={t("admin.article.select")}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <Tag className="h-3 w-3" /> {t("admin.article.tags")}
                  </label>
                  <MultiSelectDropdown
                    options={etiquetteList.map((e: any) => ({
                      value: e.id,
                      label: locale === "fr" ? e.libelleFr : e.libelleEn,
                    }))}
                    selected={selectedEtiquettes}
                    onChange={setSelectedEtiquettes}
                    placeholder={t("admin.article.select")}
                  />
                  {etiquetteList.length === 0 && (
                    <span className="text-xs text-muted-foreground italic">{t("admin.article.noTags")}</span>
                  )}
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("admin.common.col.type")}
                    </label>
                    <select
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                    >
                      {typeArticleList.map((ta: any) => (
                        <option key={ta.id} value={ta.code}>
                          {locale === "fr" ? ta.libelle : (ta.libelleEn || ta.libelle)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("admin.article.signature")}
                    </label>
                    <select
                      value={signature}
                      onChange={(e) => setSignature(e.target.value)}
                      className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                    >
                      {SIGNATURE_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("admin.article.auteur")}
                  </label>
                  <div className="w-full rounded-md border border-border bg-surface/50 px-3 py-2 text-sm text-muted-foreground">
                    {t("label.byDesk")}
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={commentairesAutorises}
                    onChange={(e) => setCommentairesAutorises(e.target.checked)}
                  />
                  {t("admin.article.commentsAllowed")}
                </label>

                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={subventionne}
                    onChange={(e) => setSubventionne(e.target.checked)}
                  />
                  Article subventionné
                </label>

                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={assisteParIa}
                    onChange={(e) => setAssisteParIa(e.target.checked)}
                  />
                  {t("admin.article.aiAssisted")}
                </label>
              </div>
            </ModulePanel>

            <ModulePanel title={t("admin.article.coverImage")}>
              {imageCouverture ? (
                <div className="relative">
                  {coverImageError ? (
                    <div className="aspect-video w-full rounded-md border border-dashed border-alert/40 bg-alert/5 p-4 text-xs text-alert space-y-2 overflow-hidden">
                      <p className="font-semibold">
                        L'image a été téléversée mais n'a pas pu être chargée depuis cette adresse (fichier privé, supprimé, ou URL invalide) :
                      </p>
                      <a
                        href={imageCouverture}
                        target="_blank"
                        rel="noreferrer"
                        className="block break-all underline"
                      >
                        {imageCouverture}
                      </a>
                      <p className="text-muted-foreground">
                        Ouvrez ce lien dans un nouvel onglet pour voir l'erreur exacte renvoyée (Accès refusé, fichier introuvable…).
                      </p>
                    </div>
                  ) : (
                    <img
                      src={imageCouverture}
                      alt=""
                      className="aspect-video w-full rounded-md object-cover"
                      onError={() => setCoverImageError(true)}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => { setImageCouverture(""); setCoverImageError(false); }}
                    className="absolute right-2 top-2 rounded-full bg-background/90 p-1 text-foreground hover:bg-background"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <label className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-border bg-surface/40 px-5 py-6 text-muted-foreground hover:border-foreground hover:text-foreground hover:bg-surface transition-colors">
                  {uploadImage.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImageIcon className="h-5 w-5" />}
                  <span className="text-xs font-semibold">{t("admin.article.chooseCover")}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} disabled={uploadImage.isPending} />
                </label>
              )}
            </ModulePanel>

            <ModulePanel title="SEO">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("admin.article.metaTitre")}
                  </label>
                  <input
                    value={metaTitre}
                    onChange={(e) => setMetaTitre(e.target.value)}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("admin.article.metaDescription")}
                  </label>
                  <textarea
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    rows={3}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-foreground resize-none"
                  />
                </div>
                {slugApercu && (
                  <div className="rounded-md border border-border bg-surface/50 px-3 py-2">
                    <div className="text-xs font-medium text-verified truncate">{slugApercu}</div>
                    {metaDescription && <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{metaDescription}</div>}
                  </div>
                )}
              </div>
            </ModulePanel>
          </div>
        </div>
      </div>
    </>
  );
}

export default ArticleNouveau;
